import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import PublicShell from "@/components/public/PublicShell";
import { Check, ArrowRight, Sparkles, Building2 } from "lucide-react";

/* ═════════════════════════════════════════════════════════════════════
   Public /pricing page — consumes the central /api/plans catalog so
   both landing and this page (and the authenticated Plan & Billing
   settings) stay in sync.
   ═════════════════════════════════════════════════════════════════════ */

export default function PricingPublic() {
    const [plans, setPlans] = useState([]);
    const [billing, setBilling] = useState("monthly");
    const [loading, setLoading] = useState(true);
    const { t, currency } = useLocale();

    useEffect(() => {
        api.get("/plans")
            .then((r) => setPlans(r.data?.plans || []))
            .catch(() => setPlans([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <PublicShell>
            <style>{styles}</style>
            <div className="pr-container" data-testid="pricing-page-public">
                <div className="pr-header">
                    <div className="pr-eyebrow">PRICING</div>
                    <h1 className="pr-title">Simple plans. No surprises.</h1>
                    <p className="pr-sub">One central catalog powers pricing everywhere in VAULT.</p>

                    <div className="pr-toggle" role="group" aria-label="Billing period">
                        <button
                            className={`pr-toggle-btn ${billing === "monthly" ? "pr-toggle-btn--active" : ""}`}
                            onClick={() => setBilling("monthly")}
                            data-testid="pricing-monthly"
                        >{t("pricing.monthly")}</button>
                        <button
                            className={`pr-toggle-btn ${billing === "annual" ? "pr-toggle-btn--active" : ""}`}
                            onClick={() => setBilling("annual")}
                            data-testid="pricing-annual"
                        >{t("pricing.annual")}</button>
                    </div>
                    {currency !== "INR" && (
                        <div className="pr-fx-note" data-testid="pricing-fx-note">
                            Displayed in {currency}. {t("pricing.billed_in_inr")}.
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="pr-skel-grid">
                        {[1, 2, 3, 4].map((i) => <div key={i} className="pr-skel" />)}
                    </div>
                ) : plans.length === 0 ? (
                    <div className="pr-empty">Pricing under final review.</div>
                ) : (
                    <div className="pr-grid">
                        {plans.map((p) => <PlanCard key={p.id} plan={p} billing={billing} />)}
                    </div>
                )}

                <div className="pr-fineprint">
                    <div className="pr-fineprint-row"><span>Taxes</span><span>Excluded unless marked inclusive</span></div>
                    <div className="pr-fineprint-row"><span>Trial</span><span>See plan details (no card required)</span></div>
                    <div className="pr-fineprint-row"><span>Auto-renewal</span><span>Yes — cancel anytime from Settings → Plan &amp; Billing</span></div>
                    <div className="pr-fineprint-row"><span>Payment provider</span><span>Secure hosted checkout</span></div>
                    <div className="pr-fineprint-row"><span>Refunds</span><span>See our <Link to="/refund-policy">Refund & Cancellation Policy</Link></span></div>
                </div>
            </div>
        </PublicShell>
    );
}

function PlanCard({ plan, billing }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const { t, fmtPrice } = useLocale();
    const [busy, setBusy] = useState(false);
    const featured = plan.highlight === "Most popular" || plan.id === "professional" || plan.id === "pro";
    const isEnterprise = ["enterprise", "business", "ca_advisor"].includes(plan.id);
    const isFree = plan.monthly_price_inr === 0;

    const price = billing === "annual" && plan.annual_price_inr ? plan.annual_price_inr : plan.monthly_price_inr;
    const priceLabel = price == null ? t("pricing.custom") : fmtPrice(price);
    const period = billing === "annual" ? t("pricing.per_year") : plan.monthly_price_inr != null && plan.monthly_price_inr > 0 ? t("pricing.per_month") : "";

    const handleClick = async () => {
        if (isEnterprise) { nav(`/contact-sales?plan=${plan.id}`); return; }
        if (!user) { nav(`/signup?plan=${plan.id}`); return; }
        if (isFree) return;
        setBusy(true);
        try {
            const { data } = await api.post("/billing/checkout", {
                package_id: `${plan.id}_${billing}`,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch {
            setBusy(false);
        }
    };

    return (
        <div className={`pr-card ${featured ? "pr-card--featured" : ""}`} data-testid={`plan-${plan.id}`}>
            {featured && (
                <div className="pr-badge"><Sparkles size={11} /> Recommended</div>
            )}
            <div className="pr-card-name">{plan.name}</div>
            <div className="pr-card-tagline">{plan.tagline}</div>
            <div className="pr-card-price">
                <span className="pr-card-value" data-testid={`plan-price-${plan.id}`}>{priceLabel}</span>
                {period && <span className="pr-card-period">{period}</span>}
            </div>
            {plan.trial_days > 0 && (
                <div className="pr-card-trial">{plan.trial_days}-day free trial</div>
            )}
            <ul className="pr-card-features">
                {(plan.features || []).slice(0, 5).map((f) => (
                    <li key={f}><Check size={13} className="pr-card-check" /> {f}</li>
                ))}
            </ul>
            <button className={`pr-card-cta ${featured ? "pr-card-cta--gold" : ""}`} onClick={handleClick} disabled={busy} data-testid={`plan-cta-${plan.id}`}>
                {isEnterprise && <Building2 size={13} />}
                {busy ? "Redirecting…" : (isEnterprise ? "Contact sales" : plan.cta || "Get started")}
                <ArrowRight size={13} />
            </button>
        </div>
    );
}

const styles = `
.pr-container { max-width: 1320px; margin: 0 auto; padding: 80px 28px 120px; color: #F6F3EC; }
@media (min-width: 768px) { .pr-container { padding-top: 120px; } }
.pr-header { text-align: center; max-width: 640px; margin: 0 auto 60px; }
.pr-eyebrow { font-size: 10.5px; font-weight: 700; letter-spacing: 0.24em; color: #C6A86B; text-transform: uppercase; }
.pr-title { margin-top: 12px; font-size: 42px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; }
@media (min-width: 768px) { .pr-title { font-size: 56px; } }
.pr-sub { margin-top: 14px; font-size: 16px; color: rgba(246,243,236,0.72); }
.pr-toggle { display: inline-flex; margin-top: 30px; padding: 4px; border-radius: 999px; background: rgba(246,243,236,0.06); border: 1px solid rgba(246,243,236,0.08); }
.pr-toggle-btn { border: none; background: transparent; color: rgba(246,243,236,0.7); font-size: 13px; font-weight: 600; padding: 8px 20px; border-radius: 999px; cursor: pointer; }
.pr-toggle-btn--active { background: #F6F3EC; color: #0B1122; }

.pr-grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
@media (min-width: 640px)  { .pr-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 900px)  { .pr-grid { grid-template-columns: repeat(3, 1fr); } }
@media (min-width: 1080px) { .pr-grid { grid-template-columns: repeat(5, 1fr); gap: 12px; } }
.pr-skel-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; }
.pr-skel { height: 380px; border-radius: 22px; background: linear-gradient(90deg, rgba(246,243,236,0.04), rgba(246,243,236,0.08), rgba(246,243,236,0.04)); background-size: 200% 100%; animation: pr-shimmer 1.6s linear infinite; }
@keyframes pr-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.pr-empty { text-align: center; padding: 80px 20px; color: rgba(246,243,236,0.55); font-size: 15px; }

.pr-card { position: relative; background: rgba(246,243,236,0.03); border: 1px solid rgba(246,243,236,0.08); border-radius: 18px; padding: 20px 18px; display: flex; flex-direction: column; transition: border-color .15s, transform .15s; }
.pr-card:hover { border-color: rgba(246,243,236,0.2); transform: translateY(-2px); }
.pr-card--featured { border-color: #C6A86B; background: rgba(198,168,107,0.06); }
.pr-badge { position: absolute; top: -11px; left: 50%; transform: translateX(-50%); background: #C6A86B; color: #0B1122; font-size: 9px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; padding: 4px 9px; border-radius: 999px; display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
.pr-card-name { font-size: 10px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: rgba(246,243,236,0.55); }
.pr-card-tagline { margin-top: 6px; font-size: 12px; color: rgba(246,243,236,0.7); min-height: 48px; line-height: 1.4; }
.pr-card-price { margin-top: 14px; display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
.pr-card-value { font-size: 26px; font-weight: 800; color: #F6F3EC; letter-spacing: -0.02em; }
.pr-card-period { font-size: 11.5px; color: rgba(246,243,236,0.55); }
.pr-card-trial { margin-top: 5px; font-size: 11px; color: #29C58A; font-weight: 600; }
.pr-card-features { margin: 16px 0 18px; list-style: none; padding: 0; display: flex; flex-direction: column; gap: 7px; min-height: 160px; }
.pr-card-features li { display: flex; align-items: flex-start; gap: 7px; font-size: 11.5px; color: rgba(246,243,236,0.82); line-height: 1.4; }
.pr-card-check { color: #29C58A; margin-top: 3px; flex-shrink: 0; width: 11px; height: 11px; }
.pr-card-cta { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 10px 14px; border-radius: 999px; font-size: 12px; font-weight: 700; border: 1px solid rgba(246,243,236,0.14); background: transparent; color: #F6F3EC; cursor: pointer; transition: background .15s, border-color .15s; }
.pr-card-cta:hover { border-color: rgba(246,243,236,0.35); }
.pr-card-cta--gold { background: #C6A86B; color: #0B1122; border-color: #C6A86B; }
.pr-card-cta--gold:hover { background: #d3b978; }

.pr-fx-note { margin-top: 14px; font-size: 12px; color: rgba(198, 168, 107, 0.85); letter-spacing: 0.02em; }
.pr-fineprint { max-width: 720px; margin: 60px auto 0; border: 1px solid rgba(246,243,236,0.08); border-radius: 18px; overflow: hidden; }
.pr-fineprint-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid rgba(246,243,236,0.06); font-size: 13px; }
.pr-fineprint-row:last-child { border-bottom: none; }
.pr-fineprint-row > span:first-child { color: rgba(246,243,236,0.55); font-weight: 600; letter-spacing: 0.02em; }
.pr-fineprint-row > span:last-child, .pr-fineprint-row a { color: rgba(246,243,236,0.85); }
.pr-fineprint-row a { color: #C6A86B; }
`;
